'use client';

import { useEffect, useRef, useState, type FormEvent } from 'react';

import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';

/**
 * Validates an email address using the browser's native <input type="email">
 * parser — the same rules that apply to a native email input field, which
 * accepts single-label domains like user@localhost that a strict dot-in-domain
 * regex would reject.
 */
function isValidEmailFormat(value: string): boolean {
  const el = document.createElement('input');
  el.type = 'email';
  el.value = value;
  return el.checkValidity();
}

interface GuestUserModalProps {
  onSubmit: (name: string, email?: string) => void;
  onDismiss?: () => void;
  isSubmitting?: boolean;
}

export function GuestUserModal({
  onSubmit,
  onDismiss,
  isSubmitting = false,
}: GuestUserModalProps) {
  const t = useTranslations();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const emailValue = email.trim();
  const emailInvalid =
    emailTouched && emailValue.length > 0 && !isValidEmailFormat(emailValue);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    if (emailValue.length > 0 && !isValidEmailFormat(emailValue)) {
      setEmailTouched(true);
      return;
    }
    onSubmit(name.trim(), emailValue || undefined);
  }

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={t('get-feedback-guestModal-ariaLabel')}
    >
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        onClick={onDismiss}
      />

      <div className="relative z-10 w-full max-w-lg rounded-2xl border border-border bg-background shadow-2xl mx-4 overflow-hidden">
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Close"
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="px-8 pt-8 pb-8">
          <p className="text-sm text-foreground text-center">
            {t('get-feedback-guestModal-greeting')}
          </p>
          <h2
            className="mt-3 text-2xl font-semibold leading-6 tracking-normal text-foreground text-center"
            style={{ fontFamily: 'Madefor' }}
          >
            {t('get-feedback-guestModal-title')}
          </h2>
          <p className="mt-[18px] font-sans text-base font-normal leading-6 tracking-normal text-black text-center">
            {t('get-feedback-guestModal-description-line1')}
            <br />
            {t('get-feedback-guestModal-description-line2')}
          </p>
        </div>

        <hr className="border-border mx-8" />

        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-6">
          <div>
            <label
              htmlFor="anon-name"
              className="mb-1.5 block font-sans text-sm font-normal leading-5 tracking-normal text-foreground"
            >
              {t('get-feedback-guestModal-nameLabel')}
              <span className="text-red-600"> *</span>
            </label>
            <input
              id="anon-name"
              ref={nameInputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('get-feedback-guestModal-namePlaceholder')}
              required
              className="w-full h-8 rounded-lg border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[#2F5DFF]"
            />
          </div>

          <div>
            <label
              htmlFor="anon-email"
              className="mb-1.5 block font-sans text-sm font-normal leading-5 tracking-normal text-foreground"
            >
              {t('get-feedback-guestModal-emailLabel')}
            </label>
            <input
              id="anon-email"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              placeholder={t('get-feedback-guestModal-emailPlaceholder')}
              aria-invalid={emailInvalid}
              aria-describedby={
                emailInvalid ? 'anon-email-error' : 'anon-email-hint'
              }
              className={`w-full h-8 rounded-lg border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none ${emailInvalid ? 'border-[#DF3336]' : 'border-input focus:border-[#2F5DFF]'}`}
            />
            {emailInvalid ? (
              <p
                id="anon-email-error"
                className="mt-1 flex items-center gap-1.5 text-xs text-[#DF3336]"
              >
                <svg
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  className="h-4 w-4 flex-shrink-0"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9 3C12.31 3 15 5.69 15 9C15 12.31 12.31 15 9 15C5.69017 14.9998 3 12.3099 3 9C3 5.69012 5.69017 3.0002 9 3ZM9 10.25C8.58596 10.2502 8.25 10.5859 8.25 11C8.25 11.4141 8.58595 11.7498 9 11.75C9.41421 11.75 9.75 11.4142 9.75 11C9.75 10.5858 9.41421 10.25 9 10.25ZM9 6C8.59016 6.0002 8.25 6.34012 8.25 6.75V8.75C8.25 9.15988 8.59016 9.4998 9 9.5C9.41 9.5 9.75 9.16 9.75 8.75V6.75C9.75 6.34 9.41 6 9 6Z"
                    fill="#DF3336"
                  />
                </svg>
                {t('get-feedback-guestModal-emailError')}
              </p>
            ) : (
              <p
                id="anon-email-hint"
                className="mt-1 text-xs text-muted-foreground"
              >
                {t('get-feedback-guestModal-helpText')}
              </p>
            )}
          </div>

          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={!name.trim() || isSubmitting}
              className="rounded-md bg-[#151414] px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 transition-opacity disabled:cursor-not-allowed disabled:bg-[#F0EFEF] disabled:text-[#A8A6A5] disabled:hover:opacity-100"
            >
              {t('get-feedback-guestModal-continue')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
